// 待宵の花嫁「ヴェールの招待」(veilInvitation・床の帯 kind 'veil') の見た目 — 両側から挟みこむヴェールの津波 (2026-10-23・比較版)。
//
// 作者の原文 (10-23): 「内側に津波のようにヴェールカーテンが迫る技にしてほしい」「上にバサッとなびいたものが重力で落ちていくように
//   消してほしい」「縮んでいってるのが変」。見本の絵 A「両側から挟みこむ」を選んだ (答えるページ JsKLdaTP6QT8bqdnwtzLWk q1)。
//   消え方の絵コンテには「近いが少し違う」(q2・どこが違うかは不明) → 絵コンテ通りの本命と、消え方を変えた3つを並べる。
// 当たりは変えない (src/attacks.ts veilInvitation の2つの帯)。2つの帯の間の隙間 (前へ1回転がった所) が、
//   2枚の波の間に残る細い隙間になる。波は帯ごとに1枚、隙間の側 (もう1つの帯の方) へ巻き込む。
// 段:
//   ① 予告 (850ms): 帯の外から波が寄せてくる (外へ 200px → 帯の上)。立ち上がりながら近づき、最後に頂が内へ巻きはじめる。
//   ② 当たり 0〜130ms: 頂が内へ崩れ落ちる (少し行き過ぎて戻る)。床に菫の照り (弾けない合図)。
//   ③ 当たりの間: 巻いたまま、裂けた縁がはためく。
//   ④ 当たりの後: 布が上へバサッとはためき (床から離れ・巻きがほどけて縦に伸びる)、重力で垂れ落ちながら薄れる。
//      縮ませない＝布の長さは保ったまま全体が落ちる。床の照りは当たりの終わりですぐ引く。
// 消え方の4つ (?brideVeilFx=…・DEV だけ):
//   tsunami        = 絵コンテ通り (中くらいに跳ね上がり、ふつうの重さで落ちる)
//   tsunami-high   = はためきが高い (大きく跳ね上がって一度宙で止まり、それから落ちる)
//   tsunami-heavy  = 落ちるのが速い (少しだけ跳ね、濡れた布のように重く速く落ちる)
//   tsunami-float  = 薄れるのが遅い (ふわりと遅く落ち、床に着く頃まで濃いまま→床で薄れる)
// 布は1枚の帯 (背骨の曲線＋幅) を数枚重ねて描く。裂けた縁はぎざぎざ、ところどころに穴。白い線・記号は描かない。描くだけ。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'

export type VeilTsunamiKind = 'tsunami' | 'tsunami-high' | 'tsunami-heavy' | 'tsunami-float'
export const VEIL_TSUNAMI_KINDS: readonly VeilTsunamiKind[] = ['tsunami', 'tsunami-high', 'tsunami-heavy', 'tsunami-float']

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
function easeInOut(p: number): number { const x = clamp01(p); return x * x * (3 - 2 * x) }
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.6
  return 1 + (c + 1) * x * x * x + c * x * x
}
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t }

const APPROACH_PX = 200      // 予告の始めに波がいる所 (帯の外側へ)
const CRASH_MS = 130         // 当たりの 0ms から頂が崩れ落ちるまで

// 消え方。flapMs=跳ね上がる時間・flapPx=跳ね上がる高さ・hangMs=宙で止まる時間・g=重さ (px/s²)・
//   fadeFrom/fadeTo=薄れる時期 (落ちはじめからの ms)・totalMs=当たりの後に消えきるまで。
type Vanish = { flapMs: number; flapPx: number; hangMs: number; g: number; fadeFrom: number; fadeTo: number; totalMs: number }
const VANISH: Record<VeilTsunamiKind, Vanish> = {
  tsunami: { flapMs: 200, flapPx: 70, hangMs: 60, g: 900, fadeFrom: 120, fadeTo: 620, totalMs: 900 },
  'tsunami-high': { flapMs: 260, flapPx: 150, hangMs: 220, g: 900, fadeFrom: 160, fadeTo: 700, totalMs: 1200 },
  'tsunami-heavy': { flapMs: 130, flapPx: 28, hangMs: 0, g: 2300, fadeFrom: 60, fadeTo: 380, totalMs: 560 },
  'tsunami-float': { flapMs: 240, flapPx: 80, hangMs: 80, g: 380, fadeFrom: 700, fadeTo: 1050, totalMs: 1400 },
}
export function veilTsunamiLingerMs(kind: VeilTsunamiKind): number { return VANISH[kind].totalMs }

// 重ねる布 (奥→手前)。dh=高さの倍率・du=外へずらす量 (帯幅の割合)・lag=揺れの遅れ (秒)・a=濃さ・tint=地の色。
type Layer = { dh: number; du: number; lag: number; a: number; tint: string; deep: string }
const LAYERS: Layer[] = [
  { dh: 1.12, du: 0.22, lag: 0.22, a: 0.42, tint: '150,128,206', deep: '96,74,150' },
  { dh: 1.0, du: 0.08, lag: 0.1, a: 0.55, tint: '186,168,232', deep: '122,98,178' },
  { dh: 0.86, du: 0.02, lag: 0, a: 0.62, tint: '222,214,246', deep: '150,130,206' },
]

type Geom = { id: number; xStart: number; xEnd: number; w: number; cx: number; dir: 1 | -1; seed: number; H: number }

/** 帯の向き (隙間の側)。同じ回のもう1つの帯の方へ巻き込む。相手が見つからない時は、主人公が帯の中なら花嫁の方・外なら主人公の方。 */
export function veilTsunamiDir(hz: ActiveHit, hits: readonly ActiveHit[], playerX: number, bossX: number): 1 | -1 {
  const cx = (hz.xStart + hz.xEnd) / 2
  const mate = hits.find((h) => h.id !== hz.id && h.attackId === hz.attackId && h.kind === hz.kind
    && Math.abs(h.elapsedMs - hz.elapsedMs) < 40 && Math.abs((h.xStart + h.xEnd) / 2 - cx) > 1)
  const target = mate ? (mate.xStart + mate.xEnd) / 2 : playerX >= hz.xStart && playerX <= hz.xEnd ? bossX : playerX
  return target >= cx ? 1 : -1
}

const dirs = new Map<number, 1 | -1>()
function geomOf(hz: ActiveHit, dir: 1 | -1): Geom {
  const w = hz.xEnd - hz.xStart
  return { id: hz.id, xStart: hz.xStart, xEnd: hz.xEnd, w, cx: (hz.xStart + hz.xEnd) / 2, dir, seed: hz.id * 2.17 + 11.3, H: Math.min(330, 225 + w * 0.33) }
}

// 波の形。in=0 予告の始め (低く・寄りかかる) → 1 巻ききる (当たり)。flap=0..1 跳ね上がって縦に伸びる。
type Pose = { rise: number; curl: number; flap: number; lift: number; drop: number; alpha: number; tatter: number }

type P2 = { u: number; y: number }   // u = 隙間の側へ + (帯の内端=0)・y = 床から上へ +
function bez(p: P2[], t: number): P2 {
  const s = 1 - t
  const a = s * s * s, b = 3 * s * s * t, c = 3 * s * t * t, d = t * t * t
  return { u: a * p[0]!.u + b * p[1]!.u + c * p[2]!.u + d * p[3]!.u, y: a * p[0]!.y + b * p[1]!.y + c * p[2]!.y + d * p[3]!.y }
}

const N = 34   // 背骨の点の数

// 布1枚を描く (床から立つ帯＝背骨の左右に幅を持たせる)。
function drawLayer(ctx: CanvasRenderingContext2D, g: Geom, L: Layer, li: number, floorY: number, pose: Pose, timeSec: number, slideU: number): void {
  const a = L.a * pose.alpha
  if (a <= 0.01) return
  const W = g.w
  const H = g.H * L.dh * pose.rise
  if (H < 4) return
  const t = timeSec - L.lag
  const sd = g.seed + li * 3.1
  // 背骨: 床の帯の真ん中 → 頂 → 隙間の側へ巻き込んで垂れる先。flap で縦に伸びた吊り布へ近づく。
  const baseU = -W / 2 - L.du * W + slideU
  const crestU = lerp(-W * 0.62, -W * 0.18, pose.curl)
  const tipU = lerp(-W * 0.35, Math.min(22, W * 0.09), pose.curl)
  const wave: P2[] = [
    { u: baseU, y: 0 },
    { u: baseU - W * 0.1, y: H * 0.55 },
    { u: crestU, y: H * lerp(1.05, 1.42, pose.curl) },
    { u: tipU, y: H * lerp(0.95, 0.56, pose.curl) },
  ]
  // 跳ね上がった形: 背骨はほぼ縦。上の端が少し隙間の側へ、裾がはためく。
  const hang: P2[] = [
    { u: baseU + W * 0.05, y: 0 },
    { u: baseU - W * 0.04, y: H * 0.45 },
    { u: baseU + W * 0.1, y: H * 0.95 },
    { u: baseU + W * 0.22, y: H * 1.18 },
  ]
  const f = pose.flap
  const ctl = wave.map((p, i) => ({ u: lerp(p.u, hang[i]!.u, f), y: lerp(p.y, hang[i]!.y, f) }))
  // 揺れ: 背骨の先ほど大きく、ゆったり。
  for (let i = 1; i < 4; i += 1) {
    const k = i / 3
    ctl[i]!.u += Math.sin(t * 2.3 + sd + i * 1.3) * W * 0.05 * k
    ctl[i]!.y += Math.sin(t * 1.9 + sd * 1.7 + i) * H * 0.03 * k
  }
  const sp: P2[] = []
  for (let i = 0; i <= N; i += 1) sp.push(bez(ctl, i / N))
  // 幅: 床で帯の幅 → 先へ細る。吊り布は上まで幅を保つ。
  const half = (s: number): number => {
    const waveW = W * 0.5 * Math.pow(1 - s, 1.25) + 3
    const hangW = W * 0.5 * (1 - 0.3 * s - 0.45 * smooth01(0.7, 1, s)) + 3   // 上の端は裂けて細る (平らな天井にしない)
    return lerp(waveW, hangW, f)
  }
  // 画面へ: u→x (dir)・y→上。lift = 床から浮く・drop = 落ちる量。
  const X = (u: number): number => g.cx + g.dir * (u + W / 2)
  const Y = (y: number): number => floorY - y - pose.lift + pose.drop
  const outer: Array<[number, number]> = []   // 背中側 (外)
  const inner: Array<[number, number]> = []   // 腹側 (隙間の側・巻きの内)
  for (let i = 0; i <= N; i += 1) {
    const s = i / N
    const p = sp[i]!, q = sp[Math.min(N, i + 1)]!, r = sp[Math.max(0, i - 1)]!
    let tu = q.u - r.u, ty = q.y - r.y
    const len = Math.hypot(tu, ty) || 1
    tu /= len; ty /= len
    // 法線 (背骨の進む向きの左 = 外)
    const nu = -ty, ny = tu
    const hw = half(s)
    // 裂けた縁: 外の縁はぎざぎざ (先ほど深い)・時間でわずかにはためく。
    const tooth = (hash01(sd + i * 7.3) - 0.35) * (6 + 22 * s) * (0.6 + 0.4 * Math.sin(t * 7 + i * 1.7))
    const ho = hw + Math.max(0, tooth)
    const hi = hw * (s < 0.12 && f < 0.5 ? 1 : 0.92) + (s > 0.5 ? Math.max(0, tooth) * 0.5 : 0) - (s > 0.8 ? hash01(sd + i * 4.1) * hw * 0.5 * f : 0)
    outer.push([X(p.u + nu * ho), Y(p.y + ny * ho)])
    inner.push([X(p.u - nu * hi), Y(p.y - ny * hi)])
  }
  // 裾 (床側の端): 打ち寄せている間は床に沿う。跳ね上がると裂けた裾が縦の房になって垂れる。
  const hem: Array<[number, number]> = []
  const nh = 10
  for (let j = 0; j <= nh; j += 1) {
    const k = j / nh
    const [ix, iy] = inner[0]!, [ox, oy] = outer[0]!
    const x = lerp(ix, ox, k), y0 = lerp(iy, oy, k)
    const strand = (j % 2 === 1 ? 1 : 0.25) * (14 + 30 * hash01(sd + j * 3.7)) * pose.tatter
    hem.push([x + Math.sin(t * 5 + j) * 3 * pose.tatter, y0 + strand])
  }
  ctx.save()
  // 床より下は描かない (落ちた布は床で消える)。帯の外側は予告の寄せの分だけ描いてよい。
  ctx.beginPath()
  ctx.rect(0, -1000, 4000, floorY + 3 + 1000)
  ctx.clip()
  ctx.beginPath()
  ctx.moveTo(inner[0]![0], inner[0]![1])
  for (let i = 1; i <= N; i += 1) ctx.lineTo(inner[i]![0], inner[i]![1])
  for (let i = N; i >= 0; i -= 1) ctx.lineTo(outer[i]![0], outer[i]![1])
  for (let j = nh; j >= 0; j -= 1) ctx.lineTo(hem[j]![0], hem[j]![1])
  ctx.closePath()
  // 穴: 裂けた布の細い裂け目 (背骨に沿って細長く・奥の布だけ・1つ)。丸い穴は染みに見えたので細長くする。evenodd で抜く。
  for (let h = 0; h < (li === 0 ? 0 : 1); h += 1) {
    const s = 0.5 + 0.35 * hash01(sd + h * 5.9)
    const i = Math.round(s * N)
    const p = sp[i]!
    const hw = half(s)
    const q = sp[Math.min(N, i + 1)]!, r = sp[Math.max(0, i - 1)]!
    const len = Math.hypot(q.u - r.u, q.y - r.y) || 1
    const nu = -(q.y - r.y) / len, ny = (q.u - r.u) / len
    const off = hw * (0.25 + 0.4 * hash01(sd + h * 2.3))
    const cxh = X(p.u + nu * off), cyh = Y(p.y + ny * off)
    const rad = Math.min(hw * 0.18, 3 + 4 * hash01(sd + h * 8.1))
    if (rad < 2.5) continue
    // 背骨の向き (画面上) に沿って 4〜5 倍に伸ばした裂け目。
    const tx = g.dir * (q.u - r.u) / len, ty = -(q.y - r.y) / len
    const along = rad * (4 + hash01(sd + h * 3.3))
    ctx.moveTo(cxh + tx * along, cyh + ty * along)
    for (let k = 1; k < 8; k += 1) {
      const an = (k / 8) * Math.PI * 2
      const rr = 0.6 + 0.5 * hash01(sd + h * 13 + k)
      const a1 = Math.cos(an) * along * rr, b1 = Math.sin(an) * rad * rr
      ctx.lineTo(cxh + tx * a1 - ty * b1, cyh + ty * a1 + tx * b1)
    }
    ctx.closePath()
  }
  // 地: 床側は菫が深く、上へ明るく透ける。
  const top = Math.min(...outer.map((p) => p[1]), ...inner.map((p) => p[1]))
  const gr = ctx.createLinearGradient(0, Y(0), 0, top)
  gr.addColorStop(0, `rgba(${swc('unparry', L.deep)},${(0.85 * a).toFixed(3)})`)
  gr.addColorStop(0.45, `rgba(${L.tint},${(0.62 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${L.tint},${(0.78 * a).toFixed(3)})`)
  ctx.fillStyle = gr
  ctx.fill('evenodd')
  // 襞: 背骨に沿った明るい帯と暗い帯 (面で・線にしない)。
  ctx.clip('evenodd')
  for (let k = 0; k < 4; k += 1) {
    const c = -0.6 + 0.4 * k + 0.12 * Math.sin(t * 1.3 + k + sd)
    const lit = k % 2 === 0
    ctx.beginPath()
    for (let i = 0; i <= N; i += 1) {
      const s = i / N, p = sp[i]!
      const q = sp[Math.min(N, i + 1)]!, r = sp[Math.max(0, i - 1)]!
      const len = Math.hypot(q.u - r.u, q.y - r.y) || 1
      const nu = -(q.y - r.y) / len, ny = (q.u - r.u) / len
      const o = half(s) * (c - 0.07)
      if (i === 0) ctx.moveTo(X(p.u + nu * o), Y(p.y + ny * o)); else ctx.lineTo(X(p.u + nu * o), Y(p.y + ny * o))
    }
    for (let i = N; i >= 0; i -= 1) {
      const s = i / N, p = sp[i]!
      const q = sp[Math.min(N, i + 1)]!, r = sp[Math.max(0, i - 1)]!
      const len = Math.hypot(q.u - r.u, q.y - r.y) || 1
      const nu = -(q.y - r.y) / len, ny = (q.u - r.u) / len
      const o = half(s) * (c + 0.07 + 0.05 * (1 - s))
      ctx.lineTo(X(p.u + nu * o), Y(p.y + ny * o))
    }
    ctx.closePath()
    ctx.fillStyle = lit ? `rgba(240,234,255,${(0.16 * a).toFixed(3)})` : `rgba(40,22,70,${(0.14 * a).toFixed(3)})`
    ctx.fill()
  }
  ctx.restore()
}

// 床の下からの菫の照り (弾けない合図・影の手と同じ色)。帯の中だけ。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.beginPath()
  ctx.rect(g.xStart, floorY - h, g.w, h + 4)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(${swc('unparry', '158,116,214')},${(0.5 * a).toFixed(3)})`)
  gr.addColorStop(0.6, `rgba(${swc('unparry', '150,110,204')},${(0.18 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${swc('unparry', '150,110,204')},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h + 4)
  ctx.restore()
}

function drawWave(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, pose: Pose, timeSec: number, slideU: number): void {
  for (let i = 0; i < LAYERS.length; i += 1) drawLayer(ctx, g, LAYERS[i]!, i, floorY, pose, timeSec, slideU)
}

/** 予告 (telegraph)。帯の外から波が寄せて立ち上がり、最後に頂が内へ巻きはじめる。 */
export function drawVeilTsunamiTelegraph(ctx: CanvasRenderingContext2D, kind: VeilTsunamiKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): void {
  void kind
  dirs.set(hz.id, dir)
  const g = geomOf(hz, dir)
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const late = smooth01(0.72, 1, p)
  const pulse = p >= 0.75 ? 1 + 0.2 * Math.sin(timeSec * 14) : 1
  underGlow(ctx, g, floorY, (0.3 + 0.6 * p) * pulse, 30 + 34 * p)
  const slide = -APPROACH_PX * (1 - easeInOut(p / 0.9))
  drawWave(ctx, g, floorY, {
    rise: 0.4 + 0.5 * easeOut(p / 0.85) + 0.08 * late, curl: 0.3 * late, flap: 0, lift: 0, drop: 0,
    alpha: smooth01(0, 0.3, p) * (0.7 + 0.3 * p), tatter: 0,
  }, timeSec, slide)
}

/** 当たり (active)。 */
export function drawVeilTsunamiActive(ctx: CanvasRenderingContext2D, kind: VeilTsunamiKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): void {
  const ms = hz.elapsedMs - hz.telegraphMs
  const d = dirs.get(hz.id) ?? dir
  dirs.set(hz.id, d)
  const g = geomOf(hz, d)
  remember(kind, g, hz, timeSec - ms / 1000)
  drawAt(ctx, kind, g, floorY, ms, hz.activeMs, timeSec)
}

/** 当たりの開始からの ms での形 (テストと残りの描画で使う)。 */
export function veilTsunamiPose(kind: VeilTsunamiKind, ms: number, activeMs: number): Pose {
  const v = VANISH[kind]
  const crash = easeOutBack(ms / CRASH_MS)
  const after = ms - activeMs
  if (after <= 0) {
    return { rise: 0.98 + 0.06 * crash, curl: 0.3 + 0.7 * crash, flap: 0, lift: 0, drop: 0, alpha: 1, tatter: 0 }
  }
  // 跳ね上がる (flapMs)・宙で止まる (hangMs)・落ちる (重力)。長さは保つ (rise は変えない)。
  const up = easeOut(after / v.flapMs)
  const fallT = Math.max(0, after - v.flapMs - v.hangMs) / 1000
  const drop = 0.5 * v.g * fallT * fallT
  const fallMs = Math.max(0, after - v.flapMs - v.hangMs)
  const alpha = 1 - smooth01(v.fadeFrom, v.fadeTo, fallMs)
  return {
    rise: 1.04, curl: 1 - 0.6 * up, flap: up, lift: v.flapPx * up, drop,
    alpha: after > v.totalMs ? 0 : alpha, tatter: up,
  }
}

function drawAt(ctx: CanvasRenderingContext2D, kind: VeilTsunamiKind, g: Geom, floorY: number, ms: number, activeMs: number, timeSec: number): void {
  // 照り: 崩れ落ちで強く、当たりの終わりで 100ms のうちに引く (危険が終わったと分かる)。
  const glowA = (ms < 130 ? 1.25 - ms / 600 : 1) * (1 - smooth01(activeMs - 20, activeMs + 100, ms))
  underGlow(ctx, g, floorY, glowA, 70)
  drawWave(ctx, g, floorY, veilTsunamiPose(kind, ms, activeMs), timeSec, 0)
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { kind: VeilTsunamiKind; g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(kind: VeilTsunamiKind, g: Geom, hz: ActiveHit, startSec: number): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { kind, g, startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (跳ね上がって、垂れ落ちながら薄れる)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawVeilTsunamiLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + VANISH[tr.kind].totalMs) { traces.delete(id); dirs.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    drawAt(ctx, tr.kind, tr.g, floorY, ms, tr.activeMs, timeSec)
  }
}
